import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useGetSidebarPersmissionsQuery } from "@/services/(my-space)/userApi";
import type { ISidebarLink } from "@/types/(company)/roles.interface";
import {
  getSidebarDisplayName,
  groupByCategory,
  sortSidebarLinks,
} from "@/utils/sidebar";
import { useNavTranslation } from "@/hooks/useNavTranslation";
import { NavIcon } from "./nav-icon";
import { useSidebarMode } from "./useSidebarMode";
import { guidesRootFor } from "@/components/guides/guide-utils";
import { GUIDES_ICON } from "@/components/guides/guide-constants";
import type { INavItem, SpaceKey } from "./sidebar.types";

/**
 * @param translateModule maps the link's stable `key` to a localized label,
 *   falling back to the API-provided name for modules with no translation yet.
 */
const toNavItem = (
  item: ISidebarLink,
  space: SpaceKey,
  translateModule: (key: string, fallback: string) => string,
): INavItem => ({
  id: item.id,
  name: translateModule(item.key, getSidebarDisplayName(item, space)),
  url: `/${item.key}`,
  icon: <NavIcon src={item.icon} />,
  categoryId: item.categoryId,
});

/**
 * Builds the sidebar's navigation model from permission-filtered links:
 * per-space item lists, the active space, collapse state, module search +
 * filtering, and category grouping. Keeps the Sidebar component presentational.
 *
 * @param isDesktop viewport is desktop-width — sidebar is in-flow (icon-rail
 *   collapsible) at/above it, a fixed overlay drawer below it.
 * @param onClose closes the overlay drawer (mobile/tablet only).
 */
export function useSidebarNavigation(
  isSidebarOpen: boolean,
  isDesktop: boolean,
  onClose: () => void,
) {
  const { t } = useTranslation(["common"]);
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sidebarLinks } = useGetSidebarPersmissionsQuery();
  const { translateModule, translateCategory } = useNavTranslation();

  const { isCollapsed, isOverlay } = useSidebarMode(isSidebarOpen, isDesktop);
  const [search, setSearch] = useState("");
  // Accordion-style: at most one module's sub-tabs are expanded at a time.
  const [openItemId, setOpenItemId] = useState<number | null>(null);

  const mySpaceItems = useMemo(
    () =>
      sortSidebarLinks(sidebarLinks?.data?.individual || [], "myspace").map(
        (item) => toNavItem(item, "myspace", translateModule),
      ),
    [sidebarLinks, translateModule],
  );

  const companyItems = useMemo(
    () =>
      sortSidebarLinks(sidebarLinks?.data?.company || [], "company").map(
        (item) => toNavItem(item, "company", translateModule),
      ),
    [sidebarLinks, translateModule],
  );

  const hasMySpace = mySpaceItems.length > 0;
  const hasCompany = companyItems.length > 0;
  const showSwitcher = hasMySpace && hasCompany;

  const activeSpace: SpaceKey = !hasMySpace
    ? "company"
    : !hasCompany
      ? "myspace"
      : location.pathname === "/company" ||
          location.pathname.startsWith("/company/")
        ? "company"
        : "myspace";

  // Application Guides is for everyone, so it is not a permission-driven
  // section: it is appended client-side, after the space checks above so it
  // never makes a space look accessible. Negative id = never a section id.
  const guidesItem = useMemo<INavItem>(
    () => ({
      id: activeSpace === "company" ? -2 : -1,
      name: translateModule("guides", "Guides"),
      url: guidesRootFor(activeSpace),
      icon: <NavIcon src={GUIDES_ICON} />,
      categoryId: null,
    }),
    [activeSpace, translateModule],
  );

  const activeItems = [
    ...(activeSpace === "company" ? companyItems : mySpaceItems),
    guidesItem,
  ];

  // Filter modules by the search query (only relevant when expanded — the
  // collapsed icon-rail has no search field).
  const query = search.trim().toLowerCase();
  const filteredItems =
    isCollapsed || !query
      ? activeItems
      : activeItems.filter((item) => item.name.toLowerCase().includes(query));

  // Group items under their category headings (mirrors the admin sidebar).
  const groupedItems = groupByCategory(
    filteredItems,
    (sidebarLinks?.data?.categories ?? []).map((category) => ({
      ...category,
      name: translateCategory(category.name),
    })),
    translateCategory(t("nav:categories.other")),
  );
  // Only show headings when there's real grouping to show (expanded + >1 group).
  const showGroupHeadings = !isCollapsed && groupedItems.length > 1;

  const handleLinkClick = () => {
    if (isOverlay) onClose();
  };

  const handleSpaceSwitch = (space: SpaceKey) => {
    navigate(space === "company" ? "/company" : "/");
  };

  return {
    currentPath: location.pathname,
    currentSearch: location.search,
    isCollapsed,
    isOverlay,
    search,
    setSearch,
    query,
    showSwitcher,
    activeSpace,
    filteredItems,
    groupedItems,
    showGroupHeadings,
    openItemId,
    setOpenItemId,
    handleLinkClick,
    handleSpaceSwitch,
  };
}
